import "native-injects";
import {
    ListUnitTypesQuery,
    UnitTypePmsActualOverrideMonthModel
} from "../../../../__generated__/generated_types";
import {SimpleHoTRow} from "../../../admin/line-item-management/SimpleHoTWrapper";
import {UnitTypeInfo, UnitTypePMSInfo} from "./types";

export const COL_SELECT = 0;
export const COL_NAME = 1;
export const COL_UNIT_COUNT = 2;
export const COL_OCCUPANCY = 3;
export const COL_IN_PLACE_RENT = 4;

export type UnitTypeTableRow = SimpleHoTRow & {
    isCompound: false;
    values: {
        select: string;
        name: string;
        unitCount: number | null;
        occupancy: number | null;
        inPlaceRent: number | null;
    };
    extraData: {
        id: string;
        inAlert: 0 | 1;
        unitCountOriginal: number | null;
        unitCountOverride: number | null;
        occupancyOriginal: number | null;
        occupancyOverride: number | null;
        inPlaceRentOriginal: number | null;
        inPlaceRentOverride: number | null;
    };
};

export type PMSData = {
    pmsUnitTypes: UnitTypePMSInfo[];
    pmsUnitTypesAsOfDate: string;
    notUsedNames: string[];
};

export type ParsedData = {
    pmsData: PMSData | undefined;
    unitTypeTableRows: UnitTypeTableRow[];
};

export function round2(value: number | null): number | null {
    if(value === null) {
        return null;
    }
    return Math.round(value * 100) / 100;
}

function displayOccupancy(value: number | null): number | null {
    if(value === null) {
        return null;
    }
    return round2(value * 100);
}

export function parseNumericInput(value: unknown): number | null | undefined {
    if(value === null || value === "") {
        return null;
    }
    if(typeof value === "number") {
        return Number.isNaN(value) ? undefined : value;
    }
    if(typeof value === "string") {
        const parsed = parseFloat(value);
        return Number.isNaN(parsed) ? undefined : parsed;
    }
    return undefined;
}

export function parseMetricValues(
    dataValue: Pick<UnitTypePmsActualOverrideMonthModel, 'original' | 'override'> | undefined,
    asOccupancyPercent = false
): {
    value: number | null;
    original: number | null;
    override: number | null;
} {
    if(!dataValue) {
        return {value: null, original: null, override: null};
    }
    let original = dataValue.original ?? null;
    let override = dataValue.override ?? null;
    if(asOccupancyPercent) {
        original = displayOccupancy(original);
        override = displayOccupancy(override);
    } else {
        original = round2(original);
        override = round2(override);
    }
    return {
        value: override === null ? original : override,
        original,
        override
    };
}

export function columnIndex(col: number | string): number {
    if(typeof col === "number") {
        return col;
    }
    switch(col) {
        case "select": return COL_SELECT;
        case "name": return COL_NAME;
        case "unitCount": return COL_UNIT_COUNT;
        case "occupancy": return COL_OCCUPANCY;
        case "inPlaceRent": return COL_IN_PLACE_RENT;
        default: return -1;
    }
}

export function metricHasOverride(row: UnitTypeTableRow, col: number): boolean {
    if(col === COL_UNIT_COUNT) {
        return row.extraData.unitCountOverride !== null;
    }
    if(col === COL_OCCUPANCY) {
        return row.extraData.occupancyOverride !== null;
    }
    if(col === COL_IN_PLACE_RENT) {
        return row.extraData.inPlaceRentOverride !== null;
    }
    return false;
}

export function buildUnitTypeTableRowsAndPmsData(unitTypesData: ListUnitTypesQuery): ParsedData {
    let pmsData: PMSData | undefined = undefined;
    if(unitTypesData.fetchPMSUnitTypes.length > 0) {
        const maxSeenLatest = unitTypesData.fetchPMSUnitTypes.maxValue(ut => ut.seenLatest);
        pmsData = {pmsUnitTypes: [], pmsUnitTypesAsOfDate: maxSeenLatest, notUsedNames: []};
        for(const pmsUnitType of unitTypesData.fetchPMSUnitTypes) {
            if(pmsUnitType.seenLatest === maxSeenLatest) {
                pmsData.pmsUnitTypes.push(pmsUnitType);
            }
        }
        const usedNames = new Set(unitTypesData.listUnitTypes.map(ut => ut.name));
        const notUsedNames = pmsData.pmsUnitTypes
            .map(ut => ut.name)
            .filter(name => !usedNames.has(name))
            .sort((a, b) => a.localeCompare(b));
        pmsData.notUsedNames = notUsedNames;
    }

    let unitTypes: UnitTypeInfo[] = [];
    for(const unitTypeRaw of unitTypesData.listUnitTypes) {
        unitTypes.push({
            ...unitTypeRaw,
            inAlert: pmsData && !pmsData.pmsUnitTypes.find(ut => ut.name === unitTypeRaw.name) ? 1 : 0
        });
    }
    unitTypes = unitTypes.sort((a, b) => {
        if(a.inAlert === b.inAlert) {
            return a.name.localeCompare(b.name);
        }
        return b.inAlert - a.inAlert;
    });
    const actuals = unitTypesData.queryPropertyPMSActualsWithOverridesMonth;
    const unitTypeTableRows = unitTypes.map(unitType => {
        const unitCount = parseMetricValues(actuals?.unitCount.find(value => value.unitTypeId === unitType.id));
        const occupancy = parseMetricValues(actuals?.occupancy.find(value => value.unitTypeId === unitType.id), true);
        const inPlaceRent = parseMetricValues(actuals?.inPlaceRent.find(value => value.unitTypeId === unitType.id));
        return {
            isCompound: false as const,
            values: {
                select: "",
                name: unitType.name,
                unitCount: unitCount.value,
                occupancy: occupancy.value,
                inPlaceRent: inPlaceRent.value
            },
            extraData: {
                id: unitType.id,
                inAlert: unitType.inAlert,
                unitCountOriginal: unitCount.original,
                unitCountOverride: unitCount.override,
                occupancyOriginal: occupancy.original,
                occupancyOverride: occupancy.override,
                inPlaceRentOriginal: inPlaceRent.original,
                inPlaceRentOverride: inPlaceRent.override
            }
        };
    });
    return {pmsData, unitTypeTableRows};
}
